import { Card } from 'src/components/UI/Card';
import { Box, Button, Typography } from '@mui/material';
import {
  headerActionsSx,
  removeAllButtonSx,
  totalPriceSx,
  selectedCountSx,
  headerContainerSx,
  titleSx,
  duplicateCardSx,
  duplicateContentWrapperSx,
  warningBoxSx,
  warningIconSx,
  warningTextSx,
  recommendedChipSx,
  recommendedChipTextSx
} from '../styles/createCampaign';
import { CitationCampaignSx } from 'src/content/CitationBuilderV2/styles/style';
import { useCitation } from '../Providers/CitationProvider';
import { useCallback, useMemo } from 'react';

// Constants
const PRICE_PER_DUPLICATE = 0.5;
const WARNING_ICON_PATH =
  '/static/images/icons/local-seo/citation-campaign-warning.svg';
const WARNING_MESSAGE =
  'We have identified duplicate listings for this locations. Remove them now.';
const RECOMMENDED_LABEL = 'Recommended';
const TITLE = 'Remove Duplicate Listings';
const REMOVE_ALL_BUTTON_TEXT = 'Remove All';
const WHATS_INCLUDED_LABEL = "What's Included";

// Feature list data
const FEATURES = [
  'We identify & submit removal requests to site editors',
  'We monitor status & re-submit as needed',
  'You get a report of removed vs. pending duplicates'
] as const;

// Types
export interface DuplicateCitationsProps {
  onRemove: () => void;
}

// Sub-components
interface WarningBoxProps {
  message: string;
  iconPath: string;
}

const WarningBox = ({ message, iconPath }: WarningBoxProps) => (
  <Box sx={warningBoxSx}>
    <Box component="img" src={iconPath} alt="warning" sx={warningIconSx} />
    <Typography sx={warningTextSx}>{message}</Typography>
  </Box>
);

interface RecommendedChipProps {
  label: string;
}

const RecommendedChip = ({ label }: RecommendedChipProps) => (
  <Box sx={recommendedChipSx}>
    <Typography sx={recommendedChipTextSx}>{label}</Typography>
  </Box>
);

interface PriceInfoProps {
  pricePerDuplicate: number;
  totalDuplicate: number;
}

const PriceInfo = ({ pricePerDuplicate, totalDuplicate }: PriceInfoProps) => {
  const totalPrice = totalDuplicate * pricePerDuplicate;

  return (
    <>
      <Typography sx={selectedCountSx}>
        ${pricePerDuplicate} per duplicate X {totalDuplicate}
      </Typography>
      <Typography sx={totalPriceSx}>Total: ${totalPrice}</Typography>
    </>
  );
};

interface FeaturesListProps {
  features: readonly string[];
}

const FeaturesList = ({ features }: FeaturesListProps) => (
  <Box>
    <Typography sx={CitationCampaignSx.whatsIncludedLabel}>
      {WHATS_INCLUDED_LABEL}
    </Typography>
    <Box sx={CitationCampaignSx.featureItem}>
      <ul>
        {features.map((feature, index) => (
          <li key={index}>{feature}</li>
        ))}
      </ul>
    </Box>
  </Box>
);

// Main component
export const DuplicateCitations = () => {
  const { state } = useCitation();

  const handleRemoveDuplicates = useCallback(() => {
    // Add remove duplicates logic here
  }, []);

  // Calculate total duplicate count and total price
  const totalDuplicateCount = useMemo(() => {
    return state.campaignDetails?.existingCitations?.citations.length ?? 0;
  }, [state.campaignDetails?.existingCitations?.citations.length]);

  const total = useMemo(() => {
    return totalDuplicateCount * PRICE_PER_DUPLICATE;
  }, [totalDuplicateCount]);

  return (
    <Card sx={duplicateCardSx}>
      <Box sx={duplicateContentWrapperSx}>
        {/* Header */}
        <Box sx={headerContainerSx}>
          <Typography sx={titleSx}>{TITLE}</Typography>
          {total > 0 && (
            <Box sx={headerActionsSx}>
              <PriceInfo
                pricePerDuplicate={PRICE_PER_DUPLICATE}
                totalDuplicate={totalDuplicateCount}
              />
              <Button
                variant="outlined"
                color="primary"
                onClick={handleRemoveDuplicates}
                sx={removeAllButtonSx}
              >
                {REMOVE_ALL_BUTTON_TEXT}
              </Button>
            </Box>
          )}
        </Box>

        {/* Warning Box */}
        <WarningBox message={WARNING_MESSAGE} iconPath={WARNING_ICON_PATH} />

        {/* Features List */}
        <FeaturesList features={FEATURES} />
      </Box>

      {/* Recommended Chip */}
      <RecommendedChip label={RECOMMENDED_LABEL} />
    </Card>
  );
};
